<template>
  <!-- Two toggle buttons rather than a radio group: `aria-pressed` mirrors the
       `selected` class exactly (both false until a system is chosen), which is
       what the sliding indicator shows visually. Keeping them native buttons
       keeps each one a Tab stop, with no roving-tabindex/arrow-key contract to
       maintain. -->
  <div class="measurement-switcher">
    <div ref="trackEl" class="switcher-track">
      <div
        class="switcher-indicator"
        :class="{ 'is-visible': hasActive, 'with-transition': transitionEnabled }"
        :style="indicatorStyle"
      />
      <button
        ref="metricBtnEl"
        class="switcher-btn"
        :class="{ selected: hasActive && currentSystem === 'metric' }"
        :aria-pressed="hasActive && currentSystem === 'metric'"
        type="button"
        @click="select('metric')"
      >
        <TipTap v-if="fields.metricLabel" v-model="metricLabelValue" mode="single" />
        <span v-else>Metric</span>
      </button>
      <button
        ref="imperialBtnEl"
        class="switcher-btn"
        :class="{ selected: hasActive && currentSystem === 'imperial' }"
        :aria-pressed="hasActive && currentSystem === 'imperial'"
        type="button"
        @click="select('imperial')"
      >
        <TipTap v-if="fields.imperialLabel" v-model="imperialLabelValue" mode="single" />
        <span v-else>Imperial</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { usePipe } from '@render/composables/usePipe'
import { useLocale } from '@render/composables/useLocale'
import {
  composeShadow,
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import TipTap from '@render/lib/TipTap'
import type { MeasurementSwitcherFields } from './definition'

const fields = defineModel<MeasurementSwitcherFields>('fields', {
  required: true,
  default: () => ({
    measurementVariable: undefined,
    defaultValue: undefined,
    metricLabel: undefined,
    imperialLabel: undefined,
    boxBg: undefined,
    bgColor: undefined,
    bgColorHover: undefined,
    bgColorActive: undefined,
    bgColorDisabled: undefined,
    bgColorSelected: undefined,
    bgColorSelectedHover: undefined,
    bgColorSelectedActive: undefined,
    bgColorSelectedDisabled: undefined,
    textColor: undefined,
    textColorDisabled: undefined,
    textColorSelected: undefined,
    boxBorderWidth: undefined,
    boxBorderColor: undefined,
    boxBorderColorDisabled: undefined,
    borderWidth: undefined,
    borderColor: undefined,
    borderColorDisabled: undefined,
    borderColorSelected: undefined,
    borderColorSelectedDisabled: undefined,
    boxBorderRadius: undefined,
    btnBorderRadius: undefined,
    boxDropShadow: undefined,
    boxInnerShadow: undefined,
    dropShadowSelected: undefined,
    innerShadowSelected: undefined,
    minHeight: undefined,
    maxWidth: undefined,
    minWidth: undefined,
    gap: undefined,
    boxPadding: undefined,
    margin: undefined,
  }),
})

const pipe = usePipe()
const loc = useLocale()

const ready = ref(false)

const trackEl = ref<HTMLDivElement | null>(null)
const metricBtnEl = ref<HTMLButtonElement | null>(null)
const imperialBtnEl = ref<HTMLButtonElement | null>(null)

const indicatorRect = ref<{ left: number; width: number; top: number; bottom: number } | null>(null)
const transitionEnabled = ref(false)

const hasActive = computed(() => indicatorRect.value !== null)
const indicatorStyle = computed(() =>
  indicatorRect.value
    ? {
        transform: `translateX(${indicatorRect.value.left}px)`,
        width: `${indicatorRect.value.width}px`,
        top: `${indicatorRect.value.top}px`,
        bottom: `${indicatorRect.value.bottom}px`,
      }
    : {},
)

const currentSystem = computed<string | null>(() => {
  if (!ready.value) return null
  const key = fields.value.measurementVariable?.key
  if (!key) return null
  const stored = pipe.getData(`var.${key}`)
  if (stored === 'metric' || stored === 'imperial') return stored
  const def = fields.value.defaultValue
  if (def === 'metric' || def === 'imperial') return def
  return loc.defaultMeasurementSystem.value
})

function activeButton(): HTMLButtonElement | null {
  if (currentSystem.value === 'metric') return metricBtnEl.value
  if (currentSystem.value === 'imperial') return imperialBtnEl.value
  return null
}

function measure() {
  const el = activeButton()
  if (!el) {
    indicatorRect.value = null
    return
  }
  const trackHeight = trackEl.value?.clientHeight ?? 0
  indicatorRect.value = {
    left: el.offsetLeft,
    width: el.offsetWidth,
    top: el.offsetTop,
    bottom: Math.max(0, trackHeight - el.offsetTop - el.offsetHeight),
  }
}

let resizeObserver: ResizeObserver | null = null

onMounted(async () => {
  await pipe.whenReady()
  ready.value = true
  await nextTick()
  measure()
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      transitionEnabled.value = true
    }),
  )

  resizeObserver = new ResizeObserver(() => measure())
  if (trackEl.value) resizeObserver.observe(trackEl.value)
  if (metricBtnEl.value) resizeObserver.observe(metricBtnEl.value)
  if (imperialBtnEl.value) resizeObserver.observe(imperialBtnEl.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})

watch(currentSystem, async () => {
  await nextTick()
  measure()
})

function select(system: 'metric' | 'imperial') {
  if (fields.value.measurementVariable?.key) {
    pipe.setData(`var.${fields.value.measurementVariable.key}`, system)
  }
}

const metricLabelValue = computed({
  get: () => pipe.getTranslation(fields.value.metricLabel?.key),
  set: (val: object) => {
    const key = fields.value.metricLabel?.key
    if (key) pipe.setTranslation(key, val)
  },
})

const imperialLabelValue = computed({
  get: () => pipe.getTranslation(fields.value.imperialLabel?.key),
  set: (val: object) => {
    const key = fields.value.imperialLabel?.key
    if (key) pipe.setTranslation(key, val)
  },
})

const overrideBoxBg = computed(() =>
  getColorStyleData(fields.value.boxBg, 'var(--color-background-subtle)'),
)
const overrideBoxBorderRadius = computed(() =>
  getSizeStyleData(fields.value.boxBorderRadius, 'var(--radius-sm)'),
)
const overrideBoxBorderWidth = computed(() => getSizeStyleData(fields.value.boxBorderWidth, '1px'))
const overrideBoxBorderColor = computed(() =>
  getColorStyleData(fields.value.boxBorderColor, 'var(--color-background-subtle)'),
)
const overrideBoxBorderColorDisabled = computed(() =>
  getColorStyleData(fields.value.boxBorderColorDisabled, 'var(--color-background-subtle)'),
)

const overrideBgColor = computed(() =>
  getColorStyleData(fields.value.bgColor, 'var(--color-background-subtle)'),
)
const overrideBgColorHover = computed(() =>
  getColorStyleData(fields.value.bgColorHover, 'var(--color-background-subtle)'),
)
const overrideBgColorActive = computed(() =>
  getColorStyleData(fields.value.bgColorActive, 'var(--color-action-secondary-background-pressed)'),
)
const overrideBgColorDisabled = computed(() =>
  getColorStyleData(
    fields.value.bgColorDisabled,
    'var(--color-action-tertiary-background-disabled)',
  ),
)

const overrideBgColorSelected = computed(() =>
  getColorStyleData(fields.value.bgColorSelected, 'var(--color-action-primary-background)'),
)
const overrideBgColorSelectedHover = computed(() =>
  getColorStyleData(
    fields.value.bgColorSelectedHover,
    'var(--color-action-primary-background-hover)',
  ),
)
const overrideBgColorSelectedActive = computed(() =>
  getColorStyleData(
    fields.value.bgColorSelectedActive,
    'var(--color-action-primary-background-pressed)',
  ),
)
const overrideBgColorSelectedDisabled = computed(() =>
  getColorStyleData(
    fields.value.bgColorSelectedDisabled,
    'var(--color-action-tertiary-background-disabled)',
  ),
)

const overrideTextColor = computed(() =>
  getColorStyleData(fields.value.textColor, 'var(--color-text-primary)'),
)
const overrideTextColorDisabled = computed(() =>
  getColorStyleData(fields.value.textColorDisabled, 'var(--color-text-disabled)'),
)
const overrideTextColorSelected = computed(() =>
  getColorStyleData(fields.value.textColorSelected, 'var(--color-action-primary-text)'),
)

const overrideBorderWidth = computed(() => getSizeStyleData(fields.value.borderWidth, '1px'))
const overrideBorderColor = computed(() =>
  getColorStyleData(fields.value.borderColor, 'var(--color-background-subtle)'),
)
const overrideBorderColorDisabled = computed(() =>
  getColorStyleData(fields.value.borderColorDisabled, 'var(--color-background-subtle)'),
)
const overrideBorderColorSelected = computed(() =>
  getColorStyleData(fields.value.borderColorSelected, 'var(--color-action-primary-border)'),
)
const overrideBorderColorSelectedDisabled = computed(() =>
  getColorStyleData(
    fields.value.borderColorSelectedDisabled,
    'var(--color-action-primary-border-disabled)',
  ),
)

const overrideBtnBorderRadius = computed(() =>
  getSizeStyleData(fields.value.btnBorderRadius, 'var(--radius-xs)'),
)
const overrideMinHeight = computed(() => getSizeStyleData(fields.value.minHeight, '40px'))
const overrideMaxWidth = computed(() => getSizeStyleData(fields.value.maxWidth, '432px'))
const overrideMinWidth = computed(() => getSizeStyleData(fields.value.minWidth, '220px'))
const overrideGap = computed(() => getSizeStyleData(fields.value.gap, '3px'))
const overrideBoxPadding = computed(() =>
  getSpacingStyleData(fields.value.boxPadding, 'var(--space-4)'),
)
watch(overrideBoxPadding, async () => {
  await nextTick()
  measure()
})
const overrideMargin = computed(() => getSpacingStyleData(fields.value.margin, '0'))

const overrideBoxShadow = computed(() =>
  composeShadow(fields.value.boxDropShadow, fields.value.boxInnerShadow),
)
const overrideIndicatorShadow = computed(() =>
  composeShadow(
    fields.value.dropShadowSelected,
    fields.value.innerShadowSelected,
    undefined,
    'var(--shadow-inner-input-hover)',
  ),
)
const overrideIndicatorShadowActive = computed(() =>
  composeShadow(fields.value.dropShadowSelected, fields.value.innerShadowSelected),
)
</script>

<style scoped lang="scss">
.measurement-switcher {
  display: flex;
  justify-content: center;
  margin: v-bind(overrideMargin);
}

.switcher-track {
  box-sizing: border-box;
  display: inline-flex;
  position: relative;
  isolation: isolate;
  border-radius: v-bind(overrideBoxBorderRadius);
  background: v-bind(overrideBoxBg);
  border: v-bind(overrideBoxBorderWidth) solid v-bind(overrideBoxBorderColor);
  box-shadow: v-bind(overrideBoxShadow);
  padding: v-bind(overrideBoxPadding);
  gap: v-bind(overrideGap);
  max-width: v-bind(overrideMaxWidth);
  min-width: v-bind(overrideMinWidth);

  &:has(.switcher-btn:disabled) {
    border-color: v-bind(overrideBoxBorderColorDisabled);
  }
}

.switcher-indicator {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 0;
  pointer-events: none;
  border-radius: v-bind(overrideBtnBorderRadius);
  background: v-bind(overrideBgColorSelected);
  border: v-bind(overrideBorderWidth) solid v-bind(overrideBorderColorSelected);
  box-shadow: v-bind(overrideIndicatorShadow);
  opacity: 0;

  &.is-visible {
    opacity: 1;
  }

  &.with-transition {
    transition:
      transform 0.25s ease,
      width 0.25s ease,
      background-color 0.2s ease,
      border-color 0.2s ease,
      opacity 0.15s ease;
  }
}

.switcher-track:has(.switcher-btn.selected:hover) .switcher-indicator {
  background: v-bind(overrideBgColorSelectedHover);
}

.switcher-track:has(.switcher-btn.selected:active) .switcher-indicator {
  background: v-bind(overrideBgColorSelectedActive);
  box-shadow: v-bind(overrideIndicatorShadowActive);
}

.switcher-track:has(.switcher-btn.selected:disabled) .switcher-indicator {
  background: v-bind(overrideBgColorSelectedDisabled);
  border-color: v-bind(overrideBorderColorSelectedDisabled);
}

@media (prefers-reduced-motion: reduce) {
  .switcher-indicator.with-transition {
    transition: opacity 0.15s ease;
  }
}

.switcher-btn {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: var(--space-8) var(--space-12);
  min-height: v-bind(overrideMinHeight);
  border-radius: v-bind(overrideBtnBorderRadius);
  border: v-bind(overrideBorderWidth) solid transparent;
  background: transparent;
  font-family: var(--text-body-sm-family);
  font-size: var(--text-body-sm-size);
  font-weight: var(--text-body-sm-weight);
  line-height: var(--text-body-sm-line-height);
  cursor: pointer;
  color: v-bind(overrideTextColor);
  transition: color 0.2s ease;
  white-space: nowrap;
  min-width: 0;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    border-radius: v-bind(overrideBtnBorderRadius);
    border: v-bind(overrideBorderWidth) solid transparent;
    background: transparent;
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease;
  }

  &:not(.selected) {
    &::before {
      background: v-bind(overrideBgColor);
      border-color: v-bind(overrideBorderColor);
    }

    &:hover::before {
      background: v-bind(overrideBgColorHover);
    }

    &:active::before {
      background: v-bind(overrideBgColorActive);
    }

    &:disabled {
      color: v-bind(overrideTextColorDisabled);

      &::before {
        background: v-bind(overrideBgColorDisabled);
        border-color: v-bind(overrideBorderColorDisabled);
      }
    }
  }

  &.selected {
    color: v-bind(overrideTextColorSelected);

    &:disabled {
      color: v-bind(overrideTextColorDisabled);
    }
  }
}
</style>
